import React, {useEffect, useRef, useState} from "react";

import {useSlidingThumb} from "../../useSlidingThumb";
import "./GameweekSelector.css";

// True while the element is pinned by `position: sticky` (e.g. under the navbar),
// so it can get a divider that it doesn't need while sitting in the page flow.
const useIsStuck = (ref) => {
    const [isStuck, setIsStuck] = useState(false);

    useEffect(() => {
        const update = () => {
            const el = ref.current;
            if (!el) return;
            const style = window.getComputedStyle(el);
            const stuck = style.position === "sticky" &&
                el.getBoundingClientRect().top <= parseFloat(style.top) + 0.5 &&
                window.scrollY > 0;
            setIsStuck(stuck);
        };
        update();
        window.addEventListener("scroll", update, {passive: true});
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [ref]);

    return isStuck;
};

// Page-level selector showing every available gameweek at once. `children` are extra
// controls shown before the gameweeks in the same (sticky) toolbar.
// With `anchorGameweek` (e.g. the gameweek a wildcard is played in) and `anchorActive`, that
// gameweek is always marked, with the white thumb and `anchorLabel`; any other picked gameweek
// is then shown in dark blue, as a secondary selection. The anchor chip keeps room for its
// label while inactive too, so the selector doesn't change width when the anchor is toggled.
// The selected gameweek has a thumb that slides behind the chips (see useSlidingThumb) and only
// changes colour when the anchor is toggled. The anchor has its own white thumb that stays in
// place and fades in and out.
export const GameweekTabs = ({gameweek, availableGameweeks, onChange, className = "", children,
                              anchorGameweek = null, anchorLabel = null, anchorActive = true}) => {
    const ref = useRef(null);
    const trackRef = useRef(null);
    const isStuck = useIsStuck(ref);

    const anchorAvailable = availableGameweeks.includes(anchorGameweek);
    const hasAnchor = anchorActive && anchorAvailable;
    const isSecondary = hasAnchor && gameweek !== anchorGameweek;

    const selectionThumb = useSlidingThumb(trackRef, gameweek);
    const anchorThumb = useSlidingThumb(trackRef, anchorAvailable ? anchorGameweek : null);

    const tabClass = (gw) => [
        gw === gameweek && "active",
        gw === gameweek && isSecondary && "secondary",
        gw === anchorGameweek && hasAnchor && "anchor",
    ].filter(Boolean).join(" ");

    return (
        <div ref={ref} className={`gameweek-tabs noSelect ${className} ${isStuck ? "is-stuck" : ""}`}>
            {children}
            <div ref={trackRef} className="gameweek-tabs-track" role="radiogroup" aria-label="Gameweek">
                {anchorThumb && anchorAvailable &&
                    <div className={`gameweek-thumb anchor-thumb ${hasAnchor ? "visible" : ""}`}
                         style={anchorThumb} aria-hidden="true"/>}
                {selectionThumb &&
                    <div className={`gameweek-thumb ${isSecondary ? "secondary" : ""}`}
                         style={selectionThumb} aria-hidden="true"/>}
                {availableGameweeks.map((gw) => (
                    <button
                        key={gw}
                        data-key={gw}
                        role="radio"
                        aria-checked={gw === gameweek}
                        className={`gameweek-tab ${tabClass(gw)} ${gw === anchorGameweek && anchorLabel ? "has-label" : ""}`}
                        onClick={() => onChange(gw)}
                    >
                        {gw === anchorGameweek && anchorLabel ? (
                            // An invisible copy with the label sizes the chip in both states
                            <span className="gameweek-tab-content">
                                <span className="gameweek-tab-sizer" aria-hidden="true">
                                    GW{gw}<span className="gameweek-anchor-label">{anchorLabel}</span>
                                </span>
                                <span>
                                    GW{gw}{hasAnchor && <span className="gameweek-anchor-label">{anchorLabel}</span>}
                                </span>
                            </span>
                        ) : `GW${gw}`}
                    </button>
                ))}
            </div>
        </div>
    );
};
